// <line-chart> — invoiced value by month on the dashboard. JSON payload
// script + sized canvas wrapper; the element only draws.
//
//   <line-chart>
//     <script type="application/json" data-chart-data>{...}</script>
//     <div class="relative h-56"><canvas></canvas></div>
//   </line-chart>

import { Chart } from "chart.js";
import {
	chartColor,
	ensureChartTheme,
	findCanvas,
	gbp,
	onThemeChange,
	readPayload,
	withAlpha,
} from "./chart-theme";

type Payload = {
	labels: string[];
	points: number[]; // integer pence
};

class LineChart extends HTMLElement {
	#abort: AbortController | null = null;
	#chart: Chart<"line"> | null = null;

	connectedCallback() {
		this.#abort?.abort();
		this.#abort = new AbortController();

		this.#render();
		onThemeChange(this.#abort.signal, () => this.#render());
	}

	disconnectedCallback() {
		this.#abort?.abort();
		this.#chart?.destroy();
		this.#chart = null;
	}

	#render() {
		ensureChartTheme();
		const payload = readPayload<Payload>(this, "line-chart");
		const brand = chartColor("--color-brand-500");

		this.#chart?.destroy();
		this.#chart = new Chart(findCanvas(this, "line-chart"), {
			type: "line",
			data: {
				labels: payload.labels,
				datasets: [
					{
						data: payload.points,
						borderColor: brand,
						borderWidth: 2,
						pointRadius: 2.5,
						pointHoverRadius: 4.5,
						pointBackgroundColor: brand,
						tension: 0.35,
						fill: true,
						backgroundColor: withAlpha(brand, 0.12),
					},
				],
			},
			options: {
				responsive: true,
				maintainAspectRatio: false,
				layout: { padding: { top: 6 } },
				scales: {
					x: { grid: { display: false }, border: { display: false } },
					y: {
						beginAtZero: true,
						border: { display: false },
						grid: { color: withAlpha(chartColor("--color-edge"), 0.6) },
						ticks: {
							maxTicksLimit: 5,
							callback: (value) => gbp(Number(value), "compact"),
						},
					},
				},
				plugins: {
					tooltip: {
						displayColors: false,
						callbacks: {
							label: (item) => ` ${gbp(item.parsed.y ?? 0)}`,
						},
					},
				},
			},
		});
	}
}

if (!customElements.get("line-chart")) {
	customElements.define("line-chart", LineChart);
}
